<template>
  <div class="cr-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <ContextFormDialog
      v-model="showFormDialog"
      :mode="dialogMode"
      :context="selectedContext"
      :save-fn="saveContext"
      :remove-file-fn="removeContextFileRecord"
      :rename-file-fn="renameContextFileRecord"
      :existing-filenames="allContextFilenames"
      @saved="handleChanged"
    />

    <ContextConfirmDialog
      v-model="showConfirmDialog"
      :action="confirmAction"
      :context="selectedContext"
      :confirm-fn="confirmActionFn"
      @confirmed="handleChanged"
    />

    <div class="cr-panel">
      <div class="cr-panel__toolbar">
        <div class="cr-panel__toolbar-start">
          <span class="cr-panel__count">
            {{ totalContexts }} product{{ totalContexts === 1 ? '' : 's' }}
          </span>
        </div>
        <ContextsHeader
          :total-contexts="totalContexts"
          :active-count="activeCount"
          :loading="loading"
          :is-session-ready="isSessionReady"
          :is-search-active="isSearchActive"
          @add="openCreate"
          @refresh="handleRefresh"
        />
      </div>

      <ContextsList
        :contexts="paginatedContexts"
        :loading="loading"
        :is-search-active="isSearchActive"
        @view="openView"
        @edit="openEdit"
        @enable="openEnable"
        @disable="openDisable"
        @delete="openDelete"
      />

      <PortalListPagination
        class="cr-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="filteredContexts.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import ContextsHeader from '@/components/Contexts/ContextsHeader.vue'
import ContextsList from '@/components/Contexts/ContextsList.vue'
import ContextFormDialog from '@/components/Contexts/Dialogs/ContextFormDialog.vue'
import ContextConfirmDialog from '@/components/Contexts/Dialogs/ContextConfirmDialog.vue'
import {
  useContextsData,
  getContextsErrorMessage,
} from '@/composables/contexts/useContextsData'

defineOptions({ name: 'ContextsPage' })

const router = useRouter()
const snackbar = ref({})
const showFormDialog = ref(false)
const showConfirmDialog = ref(false)
const selectedContext = ref(null)
const dialogMode = ref('CREATE')
const confirmAction = ref('delete')

const {
  contexts,
  loading,
  page,
  itemsPerPage,
  pageCount,
  isSessionReady,
  filteredContexts,
  paginatedContexts,
  totalContexts,
  activeCount,
  rangeLabel,
  isSearchActive,
  loadContexts,
  saveContext,
  removeContextFileRecord,
  renameContextFileRecord,
  enableContextRecord,
  disableContextRecord,
  deleteContextRecord,
  setPage,
  setItemsPerPage,
} = useContextsData()

const confirmActionFn = computed(() => {
  if (confirmAction.value === 'enable') return enableContextRecord
  if (confirmAction.value === 'disable') return disableContextRecord
  return deleteContextRecord
})

const allContextFilenames = computed(() =>
  contexts.value.flatMap((item) =>
    (item.kb_files || []).map((file) => file.filename),
  ),
)

function showError(payload) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: 'error',
    SnackbarText: getContextsErrorMessage(payload),
  }
}

async function handleRefresh() {
  const result = await loadContexts()
  if (result && result.status === 'ERROR') {
    showError(result)
  }
}

async function handleChanged() {
  await loadContexts()

  const id = String(selectedContext.value?.context_id ?? '').trim()
  if (!id) return
  const next = contexts.value.find(
    (item) => String(item.context_id ?? '').trim() === id,
  )
  if (next) selectedContext.value = { ...next }
}

function openCreate() {
  selectedContext.value = null
  dialogMode.value = 'CREATE'
  showFormDialog.value = true
}

function openEdit(context) {
  selectedContext.value = { ...context }
  dialogMode.value = 'EDIT'
  showFormDialog.value = true
}

// Row click / "Open product" action now go straight to the product's own
// detail page (Dashboard/Reports/Settings/Form fields/Dispositions tabs)
// instead of opening a side drawer -- mirrors CampaignsPage.vue's own
// openCampaign.
function openView(context) {
  const contextId = String(context?.context_id ?? context?.id ?? '').trim()
  if (!contextId) return
  router.push({ name: 'ContextDetail', params: { contextId } })
}

function openEnable(context) {
  selectedContext.value = { ...context }
  confirmAction.value = 'enable'
  showConfirmDialog.value = true
}

function openDisable(context) {
  selectedContext.value = { ...context }
  confirmAction.value = 'disable'
  showConfirmDialog.value = true
}

function openDelete(context) {
  selectedContext.value = { ...context }
  confirmAction.value = 'delete'
  showConfirmDialog.value = true
}
</script>
